import React, { useState, useRef } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { supabase } from "../supabase";
import { Btn, Input, PageHeader, TextLink } from "../ui";
import { PM_ERRORS, pmError } from "../utils/errors";
import { BrandMark } from "./BrandMark";

// hCaptcha site key. When unset the widget doesn't render and captcha isn't
// required — lets local dev / preview envs work without provisioning a key.
// Must be set in Vercel env as REACT_APP_HCAPTCHA_SITE_KEY for production.
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || "";

// ============ LOGIN / SIGNUP PAGE (Role-Aware) ============
function LoginPage({ onLogin, onBack, initialMode = "login" }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(initialMode); // "login", "signup_pm", "signup_owner", "signup_tenant"
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  // Captcha token — Supabase rejects auth calls without it when the project
  // has Bot Protection enabled. Token is one-shot: we consume and reset after
  // every submit so the next attempt requires a fresh solve.
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const resetCaptcha = () => {
    setCaptchaToken(null);
    try { captchaRef.current?.resetCaptcha(); } catch (_) {}
  };

  // What to say when the auth call THROWS rather than returning { error }.
  //
  // supabase-js returns { error } for anything the server answered, but a
  // request that never reached the server -- offline, DNS, a blocked or
  // aborted fetch -- rejects instead. None of these handlers caught that, so
  // the throw escaped, setLoading(false) never ran, and the button sat on
  // "Please wait..." for ever with nothing on screen. From the outside that
  // is indistinguishable from a broken site: no error, no spinner ending, and
  // a page that still says Sign In.
  const describeThrow = (e) => {
    const msg = String(e?.message || e || "");
    if (/failed to fetch|networkerror|load failed|aborted/i.test(msg)) {
      return "Could not reach the server. Check your connection and try again.";
    }
    return msg || "Something went wrong. Please try again.";
  };

  const requireCaptcha = () => {
    if (HCAPTCHA_SITE_KEY && !captchaToken) {
      setError("Please complete the captcha.");
      return false;
    }
    return true;
  };

  const handleLogin = async () => {
  if (!requireCaptcha()) return;
  setLoading(true);
  setError("");
  try {
  const options = captchaToken ? { captchaToken } : undefined;
  const { error } = await supabase.auth.signInWithPassword({ email, password, options });
  if (error) {
  setError(error.message);
  resetCaptcha();
  } else {
  onLogin();
  }
  } catch (e) {
  setError(describeThrow(e));
  resetCaptcha();
  } finally {
  // ALWAYS. This is the line whose absence turned a failed request into a
  // permanently stuck button.
  setLoading(false);
  }
  };

  // Reset banner stores the exact email we sent to — NOT the live `email`
  // field. Previously the banner interpolated `{email}` which meant users
  // saw the message mutate letter-by-letter if they kept typing after
  // requesting the reset. Capture once, display a stable value.
  const [resetSentEmail, setResetSentEmail] = useState("");
  // "Forgot password?" opens a small step under the link: the address to
  // send the reset to, confirmed in its own field (the one being typed into
  // may be partial, or someone else's on a shared device). It used to be
  // the browser's prompt box, which the iPhone home-screen app cannot show.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  const handleForgotPassword = async () => {
  if (!resetOpen) { setResetEmail(email || ""); setResetOpen(true); setError(""); return; }
  if (!requireCaptcha()) return;
  const target = String(resetEmail || "").trim();
  if (!target) { setError("Enter the email address for the reset link."); return; }
  if (!/^\S+@\S+\.\S+$/.test(target)) { setError("Please enter a valid email address."); return; }
  setLoading(true);
  setError("");
  setResetSentEmail("");
  try {
  const opts = { redirectTo: window.location.origin };
  if (captchaToken) opts.captchaToken = captchaToken;
  const { error } = await supabase.auth.resetPasswordForEmail(target, opts);
  if (error) { setError(error.message); resetCaptcha(); }
  else { setResetSentEmail(target); setResetOpen(false); resetCaptcha(); }
  } catch (e) {
  setError(describeThrow(e));
  resetCaptcha();
  } finally {
  setLoading(false);
  }
  };

  const handleSignup = async (userType) => {
  if (!email || !password) { setError("Email and password are required."); return; }
  if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
  if (!name.trim()) { setError("Name is required."); return; }
  if (!requireCaptcha()) return;
  setLoading(true);
  setError("");

  // Same guard as handleLogin: any of the calls below can THROW rather than
  // return { error } -- signUp, two RPCs, a fetch and an insert. Without this
  // the throw escaped and the button stayed on "Please wait..." for ever.
  try {
  // For tenant signup: validate AND redeem invite code BEFORE creating auth account
  // This prevents orphaned auth accounts if redemption fails
  let tenantRedemption = null;
  if (userType === "tenant") {
  if (!inviteCode.trim()) { setError("Invite code is required."); setLoading(false); return; }
  // Validate invite code (but don't redeem yet)
  const { data: valResult, error: valErr } = await supabase.rpc("validate_invite_code", { p_code: inviteCode.trim().toUpperCase() });
  if (valErr || !valResult?.valid) { setError("Invalid or expired invite code."); setLoading(false); return; }
  }

  // Create auth account FIRST (before redeeming invite to prevent orphaned invites)
  const signUpArgs = {
    email, password,
    options: { data: { name: name.trim(), user_type: userType } },
  };
  if (captchaToken) signUpArgs.options.captchaToken = captchaToken;
  const { data: signupData, error: signupErr } = await supabase.auth.signUp(signUpArgs);
  if (signupErr) { pmError("PM-1009", { raw: signupErr, context: "user signup" }); setError(PM_ERRORS["PM-1009"].message); resetCaptcha(); setLoading(false); return; }

  // NOW redeem the invite (auth account exists, safe to consume).
  // If redemption loses a race, the auth user is an orphan — no
  // membership, no way to self-recover. Ask the server to delete it so
  // the tenant can try again with a fresh invite without hitting an
  // "email already registered" wall.
  if (userType === "tenant" && inviteCode) {
  const { data: redeemResult, error: redeemErr } = await supabase.rpc("redeem_invite_code", {
  p_code: inviteCode.trim().toUpperCase(),
  p_email: email.toLowerCase(),
  p_name: name.trim(),
  });
  if (redeemErr || !redeemResult?.success) {
  try {
  const { data: { session } } = await supabase.auth.getSession();
  const orphanToken = session?.access_token;
  if (orphanToken) {
  await fetch("/api/account?action=cleanup-orphan-signup", {
  method: "POST",
  headers: { "Content-Type": "application/json", "Authorization": "Bearer " + orphanToken },
  body: JSON.stringify({ inviteCode: inviteCode.trim().toUpperCase() }),
  });
  }
  await supabase.auth.signOut();
  } catch (_) { /* best-effort cleanup — don't mask the original error */ }
  setError("Invite code couldn't be redeemed: " + (redeemErr?.message || "already used") + ". Contact your property manager for a new invite.");
  setLoading(false);
  return;
  }
  tenantRedemption = redeemResult;
  }

  // No client-side membership write here on purpose. redeem_invite_code
  // is SECURITY DEFINER and has already inserted the company_members row
  // (role 'tenant', status 'active') as part of redeeming the code, so
  // this was always a duplicate. It is also the one remaining path that
  // would need a "self-insert at status=active" policy -- precisely the
  // hole that let any authenticated user join any company as admin.

  // Save user_type to app_users -- but only when a session exists whose
  // email matches this row. app_users_safe_insert requires exactly that
  // (or a company you already belong to), so every other case is a write
  // the policy is certain to reject: signing up while email confirmation
  // is pending leaves no session at all, and signing someone up while a
  // different account is signed in fails the email comparison. Both
  // reached nobody but Sentry, four times since April.
  //
  // Skipping loses nothing. user_type is already written to the auth
  // user's metadata by signUp above, no code reads app_users.user_type,
  // and the row itself is created with its company_id once a company is
  // selected.
  const { data: { session: signupSession } } = await supabase.auth.getSession();
  const signupSessionEmail = (signupSession?.user?.email || "").toLowerCase();
  if (signupSessionEmail && signupSessionEmail === email.toLowerCase()) {
  const { error: appUserErr } = await supabase.from("app_users").insert([{
  email: email.toLowerCase(), name: name.trim(), role: userType === "tenant" ? "tenant" : userType === "owner" ? "owner" : "pm",
  user_type: userType,
  }]).select();
  if (appUserErr && !appUserErr.message.includes("duplicate")) { pmError("PM-1009", { raw: appUserErr, context: "app_users write", silent: true }); }
  }

  resetCaptcha();
  setSignupSuccess(true);
  } catch (e) {
  setError(describeThrow(e));
  resetCaptcha();
  } finally {
  setLoading(false);
  }
  };

  const userTypeLabels = {
  signup_pm: { title: "Property Manager Sign Up", subtitle: "Create your management account", color: "indigo", icon: "\u{1F3E2}" },
  signup_owner: { title: "Property Owner Sign Up", subtitle: "Create your owner account", color: "emerald", icon: "\u{1F3E0}" },
  signup_tenant: { title: "Tenant Sign Up", subtitle: "Join with your invite code", color: "amber", icon: "\u{1F511}" },
  };

  if (signupSuccess) {
  return (
  <div className="min-h-dvh safe-y safe-x bg-gradient-to-br from-brand-50 to-white flex flex-col">
  <nav className="flex items-center justify-between px-8 py-4">
  <button onClick={onBack} className="flex items-center gap-2 text-xl font-bold text-brand-700">
    <BrandMark size={32} className="shrink-0 drop-shadow-sm" />
    <span>Housify</span>
  </button>
  </nav>
  <div className="flex-1 flex items-center justify-center px-4">
  <div className="bg-white rounded-xl border border-neutral-200 shadow-card p-8 w-full max-w-sm text-center">
  <div className="text-4xl mb-3">{"\u2705"}</div>
  <PageHeader title="Account Created!" />
  <p className="text-sm text-neutral-400 mb-4">Check your email for a confirmation link. Once confirmed, you can sign in.</p>
  <Btn size="lg" onClick={() => { setSignupSuccess(false); setMode("login"); setError(""); }}>Back to Sign In</Btn>
  </div>
  </div>
  </div>
  );
  }

  const isSignup = mode.startsWith("signup_");
  const typeInfo = userTypeLabels[mode] || {};

  return (
  <div className="min-h-dvh safe-y safe-x bg-gradient-to-br from-brand-50 to-white flex flex-col">
  <nav className="flex items-center justify-between px-8 py-4">
  <button onClick={onBack} className="flex items-center gap-2 text-xl font-bold text-brand-700">
    <BrandMark size={32} className="shrink-0 drop-shadow-sm" />
    <span>Housify</span>
  </button>
  </nav>
  <div className="flex-1 flex items-center justify-center px-4">
  <div className="bg-white rounded-xl border border-neutral-200 shadow-card p-8 w-full max-w-sm">
  {isSignup && (
  <div className="text-center mb-4">
  <span className="text-3xl">{typeInfo.icon}</span>
  <PageHeader title={typeInfo.title} />
  <p className="text-sm text-neutral-400">{typeInfo.subtitle}</p>
  </div>
  )}
  {!isSignup && (
  <>
  <PageHeader title="Welcome back" />
  <p className="text-sm text-neutral-400 mb-6">Sign in to your account</p>
  </>
  )}
  {error && <div className="bg-danger-50 text-danger-600 text-xs rounded-lg px-3 py-2 mb-4">{error}</div>}
  {resetSentEmail && <div className="bg-positive-50 text-positive-700 text-xs rounded-lg px-3 py-2 mb-4">Password reset link sent to {resetSentEmail}. Check your inbox.</div>}

  {isSignup && (
  <div className="mb-4">
  <label className="text-xs font-medium text-neutral-500 block mb-1">Full Name</label>
  <Input value={name} onChange={e => setName(e.target.value)} placeholder="John Smith" />
  </div>
  )}
  <div className="mb-4">
  <label className="text-xs font-medium text-neutral-500 block mb-1">Email</label>
  <Input type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
  </div>
  <div className="mb-4">
  <label className="text-xs font-medium text-neutral-500 block mb-1">Password</label>
  <Input type="password" autoComplete={isSignup ? "new-password" : "current-password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" onKeyDown={e => e.key === "Enter" && (isSignup ? handleSignup(mode.replace("signup_", "")) : handleLogin())} />
  </div>

  {mode === "signup_tenant" && (
  <div className="mb-4">
  <label className="text-xs font-medium text-neutral-500 block mb-1">Invite Code *</label>
  <Input value={inviteCode} onChange={e => setInviteCode(e.target.value.toUpperCase())} placeholder="e.g. TNT-38472916" className="bg-warn-50 tnum tracking-wider" />
  <p className="text-xs text-neutral-400 mt-1">Check your invite email from your landlord or property manager</p>
  </div>
  )}

  {HCAPTCHA_SITE_KEY && (
  <div className="mb-4 flex justify-center">
    <HCaptcha
      ref={captchaRef}
      sitekey={HCAPTCHA_SITE_KEY}
      onVerify={setCaptchaToken}
      onExpire={() => setCaptchaToken(null)}
      onError={() => setCaptchaToken(null)}
    />
  </div>
  )}

  <Btn
    size="lg"
    variant={isSignup ? (mode === "signup_owner" ? "success-fill" : mode === "signup_tenant" ? "warning-fill" : "primary") : "primary"}
    onClick={isSignup ? () => handleSignup(mode.replace("signup_", "")) : handleLogin}
    disabled={loading}
    className="w-full"
  >
  {loading ? "Please wait..." : isSignup ? "Create Account" : "Sign In"}
  </Btn>

  <div className="text-center mt-4 space-y-2">
  {isSignup ? (
  <TextLink tone="brand" size="xs" onClick={() => { setMode("login"); setError(""); setResetSentEmail(""); resetCaptcha(); }}>Already have an account? Sign in</TextLink>
  ) : (
  <>
  {!resetOpen && <TextLink tone="neutral" size="xs" onClick={handleForgotPassword} disabled={loading}>Forgot password?</TextLink>}
  {resetOpen && (
  <div className="w-full mt-2 rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-2">
    <label htmlFor="reset-email" className="text-xs font-medium text-neutral-600 block">Send a password reset link to</label>
    <Input id="reset-email" type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder="you@example.com" autoFocus onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleForgotPassword(); } }} />
    <div className="flex gap-2 justify-end">
      <Btn variant="slate" size="sm" onClick={() => setResetOpen(false)} disabled={loading}>Cancel</Btn>
      <Btn size="sm" onClick={handleForgotPassword} disabled={loading}>Send link</Btn>
    </div>
  </div>
  )}
  <TextLink className="block mx-auto" onClick={onBack}>Back to role selection</TextLink>
  </>
  )}
  {/* Shown to everyone who signs in (Plaid security review, 2026-10-23). */}
  <p className="text-2xs text-neutral-400 pt-2">
  <a href="https://housifyholdings.housify365.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-neutral-600">Privacy Policy</a>
  {" · "}
  <a href="https://housifyholdings.housify365.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-neutral-600">Terms</a>
  </p>
  </div>
  </div>
  </div>
  </div>
  );
}

export { LoginPage };
